import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LogOut, Mail, Phone, User, Calendar, RefreshCw, MessageSquare, Shield, UserPlus, Copy, Check, Search, ToggleLeft, ToggleRight, ClipboardList, Trash2, BookOpen, Dumbbell, UtensilsCrossed, Send, CalendarCheck } from 'lucide-react';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8000'
  : (process.env.REACT_APP_BACKEND_URL || '');

const ALLOWED_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || process.env.REACT_APP_ADMIN_EMAIL || '')
  .split(',').map(e => e.trim()).filter(Boolean);

const Admin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(() => sessionStorage.getItem('admin_token') ? 'authenticated' : 'password'); // 'password' | 'totp' | 'authenticated'
  const [pwForm, setPwForm] = useState({ email: '', password: '' });
  const [totpCode, setTotpCode] = useState('');
  const pwFormRef = useRef(pwForm);
  pwFormRef.current = pwForm; // sempre fresco a cada render
  const [token, setToken] = useState(() => sessionStorage.getItem('admin_token') || null);
  const [userEmail, setUserEmail] = useState(() => sessionStorage.getItem('admin_email') || null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [students, setStudents] = useState([]);
  const [studentForm, setStudentForm] = useState({ name: '', email: '' });
  const [studentCreated, setStudentCreated] = useState(null);
  const [studentLoading, setStudentLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentFilter, setStudentFilter] = useState('all'); // 'all' | 'active' | 'pending' | 'inactive'
  const [unreadCount, setUnreadCount] = useState(0);
  const [clearing, setClearing] = useState(false);
  const messagesRef = useRef(null);
  const [confirm, setConfirm] = useState(null);
  const [alertMsg, setAlertMsg] = useState('');

  const fetchContacts = useCallback(async (t) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/contacts`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const data = res.data;
      setContacts(data);
      // Calcula não lidas: mensagens mais novas que o último acesso
      const lastSeen = localStorage.getItem('admin_contacts_seen_at');
      if (lastSeen) {
        setUnreadCount(data.filter(c => new Date(c.submitted_at) > new Date(lastSeen)).length);
      } else {
        setUnreadCount(data.length);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem('admin_token');
        sessionStorage.removeItem('admin_email');
        setToken(null);
        setStep('password');
        setError('Sessão expirada. Faça login novamente.');
      } else {
        setError('Erro ao carregar mensagens.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const markContactsRead = () => {
    localStorage.setItem('admin_contacts_seen_at', new Date().toISOString());
    setUnreadCount(0);
  };

  const scrollToMessages = () => {
    messagesRef.current?.scrollIntoView({ behavior: 'smooth' });
    markContactsRead();
  };

  const handleClearContacts = () => {
    setConfirm({
      message: `Apagar todas as ${contacts.length} mensagens? Esta ação não pode ser desfeita.`,
      onConfirm: async () => {
        setClearing(true);
        try {
          await axios.delete(`${BACKEND_URL}/api/admin/contacts`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setContacts([]);
          setUnreadCount(0);
          markContactsRead();
        } catch {
          setAlertMsg('Erro ao limpar mensagens.');
        } finally {
          setClearing(false);
        }
      },
    });
  };

  const fetchStudents = useCallback(async (t) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/students`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setStudents(res.data);
    } catch {}
  }, []);

  const handleToggleActive = async (student) => {
    try {
      const res = await axios.patch(`${BACKEND_URL}/api/admin/students/${student.id}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents(prev => prev.map(s => s.id === student.id ? { ...s, active: res.data.active, status: res.data.status } : s));
    } catch (err) {
      setAlertMsg(err.response?.data?.detail || 'Erro ao alterar status.');
    }
  };

  const handleCreateStudent = async (e) => {
    e.preventDefault();
    setStudentLoading(true);
    setStudentCreated(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/students`, studentForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudentCreated(res.data);
      setStudentForm({ name: '', email: '' });
      fetchStudents(token);
    } catch (err) {
      setAlertMsg(err.response?.data?.detail || 'Erro ao cadastrar aluno.');
    } finally {
      setStudentLoading(false);
    }
  };

  const handleResetPassword = (student) => {
    setConfirm({
      message: `Redefinir senha de ${student.name}?`,
      onConfirm: async () => {
        try {
          const res = await axios.patch(`${BACKEND_URL}/api/admin/students/${student.id}/reset-password`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setResetResult({ name: student.name, password: res.data.password });
        } catch (err) {
          setAlertMsg(err.response?.data?.detail || 'Erro ao redefinir senha.');
        }
      },
    });
  };

  const copyPassword = () => {
    if (studentCreated?.password) {
      navigator.clipboard.writeText(studentCreated.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    if (token) {
      setStep('authenticated');
      fetchContacts(token);
      fetchStudents(token);
    }
  }, [token, fetchContacts, fetchStudents]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (ALLOWED_EMAILS.length > 0 && !ALLOWED_EMAILS.includes(pwForm.email)) {
      setError('E-mail não autorizado.');
      return;
    }
    setError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/login`, { password: pwForm.password });
      if (res.data.status === 'totp_required') {
        setStep('totp');
      } else if (res.data.token) {
        sessionStorage.setItem('admin_token', res.data.token);
        sessionStorage.setItem('admin_email', pwForm.email);
        setToken(res.data.token);
        setUserEmail(pwForm.email);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Senha incorreta.');
    }
  };

  const handleTotpSubmit = async (e, codeOverride) => {
    e.preventDefault();
    const code = codeOverride ?? totpCode;
    setError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/login`, {
        password: pwForm.password,
        totp_code: code,
      });
      if (res.data.token) {
        sessionStorage.setItem('admin_token', res.data.token);
        sessionStorage.setItem('admin_email', pwForm.email);
        setToken(res.data.token);
        setUserEmail(pwForm.email);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Código inválido.');
      setTotpCode('');
    }
  };

  useEffect(() => {
    if (step !== 'totp' || totpCode.length !== 6) return;
    const { password, email } = pwFormRef.current;
    setError('');
    axios.post(`${BACKEND_URL}/api/admin/login`, { password, totp_code: totpCode })
      .then(res => {
        if (res.data.token) {
          sessionStorage.setItem('admin_token', res.data.token);
          sessionStorage.setItem('admin_email', email);
          setToken(res.data.token);
          setUserEmail(email);
        }
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'Código inválido.');
        setTotpCode('');
      });
  }, [totpCode, step]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_email');
    setToken(null);
    setUserEmail(null);
    setStep('password');
    setContacts([]);
    setError('');
    setPwForm({ email: '', password: '' });
    setTotpCode('');
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateStr; }
  };

  // ── Tela: senha ──────────────────────────────────────────────────────────────
  if (step === 'password') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold tracking-tight uppercase mb-2">Daniel Aron</h1>
            <p className="text-[#A3A3A3] text-sm tracking-widest uppercase">Painel Administrativo</p>
          </div>
          <div className="bg-[#121212] border border-white/10 p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 text-red-400 text-sm">{error}</div>
            )}
            <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-4">
              <div>
                <label className="block text-xs text-[#A3A3A3] uppercase tracking-widest mb-2">E-mail</label>
                <input
                  type="email" required value={pwForm.email}
                  onChange={e => setPwForm({ ...pwForm, email: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-white/40 outline-none text-sm transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs text-[#A3A3A3] uppercase tracking-widest mb-2">Senha</label>
                <input
                  type="password" required value={pwForm.password}
                  onChange={e => setPwForm({ ...pwForm, password: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-white/40 outline-none text-sm transition-colors"
                />
              </div>
              <button type="submit"
                className="w-full py-3 bg-white text-black font-bold uppercase tracking-widest text-sm hover:bg-[#E5E5E5] transition-colors mt-2"
              >
                Continuar
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── Tela: código 2FA ─────────────────────────────────────────────────────────
  if (step === 'totp') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10">
            <Shield size={32} className="mx-auto mb-4 text-white opacity-50" />
            <h1 className="text-2xl font-bold tracking-tight uppercase mb-2">Verificação em 2 etapas</h1>
            <p className="text-[#A3A3A3] text-sm">Abra o Google Authenticator e digite o código de 6 dígitos.</p>
          </div>
          <div className="bg-[#121212] border border-white/10 p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 text-red-400 text-sm">{error}</div>
            )}
            <form onSubmit={handleTotpSubmit} className="flex flex-col gap-4">
              <div>
                <label className="block text-xs text-[#A3A3A3] uppercase tracking-widest mb-2">Código 2FA</label>
                <input
                  type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                  required autoFocus value={totpCode}
                  onChange={e => { const v = e.target.value.replace(/\D/g, ''); console.log('[2FA] onChange:', v); setTotpCode(v); }}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-white/40 outline-none text-center tracking-[0.5em] text-xl transition-colors"
                  placeholder="000000"
                />
              </div>
              <button type="submit"
                className="w-full py-3 bg-white text-black font-bold uppercase tracking-widest text-sm hover:bg-[#E5E5E5] transition-colors mt-2"
              >
                Entrar
              </button>
              <button type="button"
                onClick={() => { setStep('password'); setError(''); setTotpCode(''); }}
                className="text-[#A3A3A3] text-xs text-center hover:text-white transition-colors"
              >
                Voltar
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── Painel admin ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-6 lg:pt-10 pb-20">
      <div className="max-w-6xl mx-auto px-4 md:px-8">

        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight uppercase">Painel Admin</h1>
            <p className="text-[#A3A3A3] text-sm mt-1">
              Logado como <span className="text-white">{userEmail}</span>
            </p>
          </div>
          {/* Badge de mensagens */}
          <button
            onClick={scrollToMessages}
            title="Ver mensagens recebidas"
            className="relative flex items-center gap-2 px-4 py-2.5 bg-[#121212] border border-white/10 hover:border-white/30 text-sm transition-colors flex-shrink-0"
          >
            <Mail size={16} />
            <span className="hidden sm:inline text-[#A3A3A3]">Mensagens</span>
            {unreadCount > 0 && (
              <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 bg-white text-black text-xs font-bold rounded-full flex items-center justify-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <div className="bg-[#121212] border border-white/5 p-6">
            <p className="text-[#A3A3A3] text-xs tracking-widest uppercase mb-2">Total de mensagens</p>
            <p className="text-4xl font-bold">{contacts.length}</p>
          </div>
          <div className="bg-[#121212] border border-white/5 p-6">
            <p className="text-[#A3A3A3] text-xs tracking-widest uppercase mb-2">Última mensagem</p>
            <p className="text-sm font-medium">
              {contacts.length > 0 ? formatDate(contacts[0].submitted_at) : '—'}
            </p>
          </div>
          <div className="bg-[#121212] border border-white/5 p-6">
            <p className="text-[#A3A3A3] text-xs tracking-widest uppercase mb-2">Esta semana</p>
            <p className="text-4xl font-bold">
              {contacts.filter(c => {
                const d = new Date(c.submitted_at);
                const week = new Date(); week.setDate(week.getDate() - 7);
                return d >= week;
              }).length}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-10">
          <button onClick={() => { fetchContacts(token); markContactsRead(); }}
            className="flex items-center gap-2 px-5 py-3 bg-[#121212] border border-white/10 hover:border-white/30 text-sm transition-colors"
          >
            <RefreshCw size={15} /> Atualizar
          </button>
          <button onClick={() => navigate('/admin/conteudos')}
            className="flex items-center gap-2 px-5 py-3 bg-[#121212] border border-white/10 hover:border-white/30 text-sm transition-colors"
          >
            <BookOpen size={15} /> Conteúdos
          </button>
          <button onClick={() => navigate('/admin/biblioteca')}
            className="flex items-center gap-2 px-5 py-3 bg-[#121212] border border-white/10 hover:border-white/30 text-sm transition-colors"
          >
            <Dumbbell size={15} /> Biblioteca
          </button>
          <button onClick={() => navigate('/admin/despensa')}
            className="flex items-center gap-2 px-5 py-3 bg-[#121212] border border-white/10 hover:border-white/30 text-sm transition-colors"
          >
            <UtensilsCrossed size={15} /> Despensa
          </button>
          <button onClick={() => navigate('/admin/emails')}
            className="flex items-center gap-2 px-5 py-3 bg-[#121212] border border-white/10 hover:border-white/30 text-sm transition-colors"
          >
            <Send size={15} /> E-mails
          </button>
          <button onClick={() => navigate('/admin/presenca')}
            className="flex items-center gap-2 px-5 py-3 bg-[#121212] border border-white/10 hover:border-white/30 text-sm transition-colors"
          >
            <CalendarCheck size={15} /> Presença
          </button>
        </div>

        {/* ── Alunos ── */}
        <div className="mb-12">
          <h2 className="text-sm font-bold tracking-[0.2em] uppercase text-[#A3A3A3] mb-4">Alunos</h2>

          {/* Cadastro */}
          <div className="bg-[#121212] border border-white/5 p-6 mb-4">
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] mb-4 flex items-center gap-2">
              <UserPlus size={13} /> Cadastrar novo aluno
            </h3>
            <form onSubmit={handleCreateStudent} className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[160px]">
                <label className="block text-xs text-[#555] uppercase tracking-widest mb-1">Nome</label>
                <input
                  type="text" required value={studentForm.name}
                  onChange={e => setStudentForm({ ...studentForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none text-sm transition-colors"
                  placeholder="Nome completo"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-[#555] uppercase tracking-widest mb-1">E-mail</label>
                <input
                  type="email" required value={studentForm.email}
                  onChange={e => setStudentForm({ ...studentForm, email: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none text-sm transition-colors"
                  placeholder="email@exemplo.com"
                />
              </div>
              <button type="submit" disabled={studentLoading}
                className="px-5 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors disabled:opacity-50"
              >
                {studentLoading ? 'Cadastrando...' : 'Cadastrar'}
              </button>
            </form>

            {studentCreated && (
              <div className="mt-4 p-4 bg-green-900/20 border border-green-500/30 text-sm">
                <p className="text-green-400 font-medium mb-2">Aluno cadastrado: <span className="text-white">{studentCreated.name}</span></p>
                <div className="flex items-center gap-3">
                  <span className="text-[#A3A3A3]">Senha gerada:</span>
                  <code className="text-white bg-[#0A0A0A] px-3 py-1 rounded tracking-widest">{studentCreated.password}</code>
                  <button onClick={copyPassword} className="text-[#A3A3A3] hover:text-white transition-colors">
                    {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Notificação reset */}
          {resetResult && (
            <div className="mb-4 p-4 bg-yellow-900/20 border border-yellow-500/30 text-sm flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-yellow-400 font-medium">Senha de {resetResult.name} redefinida:</span>
                <code className="text-white bg-[#0A0A0A] px-3 py-1 rounded tracking-widest">{resetResult.password}</code>
                <button onClick={() => navigator.clipboard.writeText(resetResult.password)} className="text-[#A3A3A3] hover:text-white">
                  <Copy size={14} />
                </button>
              </div>
              <button onClick={() => setResetResult(null)} className="text-[#555] hover:text-white text-xs">✕</button>
            </div>
          )}

          {/* Busca e filtros */}
          <div className="flex flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2 flex-1 min-w-[200px] bg-[#121212] border border-white/10 px-3 py-2">
              <Search size={13} className="text-[#555]" />
              <input
                type="text" placeholder="Buscar por nome ou e-mail..." value={studentSearch}
                onChange={e => setStudentSearch(e.target.value)}
                className="bg-transparent outline-none text-sm flex-1 text-[#E5E5E5] placeholder-[#555]"
              />
            </div>
            <div className="flex border border-white/10 overflow-hidden">
              {[['all','Todos'], ['active','Ativos'], ['pending','Aguardando'], ['inactive','Inativos']].map(([val, label]) => (
                <button key={val} onClick={() => setStudentFilter(val)}
                  className={`px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors ${studentFilter === val ? 'bg-white text-black' : 'bg-[#121212] text-[#555] hover:text-white'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Lista */}
          {(() => {
            const q = studentSearch.toLowerCase();
            const filteredStudents = students.filter(s => {
              const matchSearch = !q || s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q);
              const st = s.status || (s.active ? 'active' : 'inactive');
              const matchFilter = studentFilter === 'all' || st === studentFilter;
              return matchSearch && matchFilter;
            });
            const statusBadge = {
              pending:  { label: 'Aguardando', cls: 'text-yellow-400 border-yellow-400/30' },
              active:   { label: 'Ativo',      cls: 'text-green-400 border-green-500/30' },
              inactive: { label: 'Inativo',    cls: 'text-[#555] border-white/10' },
            };
            return filteredStudents.length === 0 ? (
              <div className="text-center py-10 text-[#555] text-sm bg-[#121212] border border-white/5">
                Nenhum aluno encontrado.
              </div>
            ) : (
              <>
                <p className="text-xs text-[#555] mb-2">{filteredStudents.length} aluno{filteredStudents.length !== 1 ? 's' : ''} encontrado{filteredStudents.length !== 1 ? 's' : ''}</p>
                <div className="flex flex-col gap-2">
                  {filteredStudents.map(s => {
                    const st = s.status || (s.active ? 'active' : 'inactive');
                    const badge = statusBadge[st] || statusBadge.inactive;
                    return (
                <div key={s.id} className={`bg-[#121212] border px-5 py-4 flex flex-wrap items-center justify-between gap-3 transition-colors ${s.active ? 'border-white/5' : 'border-white/5 opacity-50'}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`text-xs border px-2 py-0.5 flex-shrink-0 ${badge.cls}`}>{badge.label}</span>
                    <span className="text-sm font-medium truncate">{s.name}</span>
                    <span className="text-xs text-[#555] truncate hidden sm:block">{s.email}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-[#555] hidden md:block">{formatDate(s.createdAt)}</span>
                    <button onClick={() => navigate(`/admin/alunos/${s.id}`)}
                      className="flex items-center gap-1 text-xs text-[#555] hover:text-white transition-colors border border-white/5 px-2 py-1"
                    >
                      <ClipboardList size={12} /> Planos
                    </button>
                    <button onClick={() => handleResetPassword(s)}
                      className="text-xs text-[#555] hover:text-yellow-400 transition-colors border border-white/5 px-2 py-1"
                    >
                      Redefinir senha
                    </button>
                    <button onClick={() => handleToggleActive(s)}
                      className={`flex items-center gap-1 text-xs border px-2 py-1 transition-colors ${s.active ? 'border-red-500/20 text-red-400 hover:bg-red-900/20' : 'border-green-500/20 text-green-400 hover:bg-green-900/20'}`}
                    >
                      {s.active ? <><ToggleRight size={13} /> Desativar</> : <><ToggleLeft size={13} /> Ativar</>}
                    </button>
                  </div>
                </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>

        <div ref={messagesRef}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold tracking-[0.2em] uppercase text-[#A3A3A3]">
              Mensagens recebidas
              {contacts.length > 0 && (
                <span className="ml-2 text-[#555] font-normal normal-case tracking-normal">
                  ({contacts.length}/500)
                </span>
              )}
            </h2>
            {contacts.length > 0 && (
              <button
                onClick={handleClearContacts}
                disabled={clearing}
                className="flex items-center gap-2 px-4 py-2 bg-[#121212] border border-red-500/20 text-red-400 hover:bg-red-900/20 text-xs transition-colors disabled:opacity-50"
              >
                <Trash2 size={13} />
                {clearing ? 'Limpando...' : 'Limpar tudo'}
              </button>
            )}
          </div>
          {loading && (
            <div className="text-center py-20 text-[#A3A3A3]">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-white mx-auto mb-4" />
              Carregando...
            </div>
          )}
          {error && !loading && (
            <div className="p-6 bg-red-900/20 border border-red-500/30 text-red-400 text-sm">{error}</div>
          )}
          {!loading && !error && contacts.length === 0 && (
            <div className="text-center py-20 text-[#A3A3A3]">
              <MessageSquare size={40} className="mx-auto mb-4 opacity-30" />
              Nenhuma mensagem recebida ainda.
            </div>
          )}
          {!loading && contacts.length > 0 && (
            <div className="flex flex-col gap-4">
              {contacts.map((contact) => (
                <div key={contact.id}
                  className="bg-[#121212] border border-white/5 hover:border-white/10 transition-colors p-6"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div className="flex flex-wrap gap-4">
                      <span className="flex items-center gap-2 text-sm font-semibold">
                        <User size={15} className="text-[#C0C0C0]" />{contact.name}
                      </span>
                      <a href={`mailto:${contact.email}`}
                        className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white transition-colors"
                      >
                        <Mail size={15} />{contact.email}
                      </a>
                      <a href={`https://wa.me/${contact.phone?.replace(/\D/g, '')}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white transition-colors"
                      >
                        <Phone size={15} />{contact.phone}
                      </a>
                    </div>
                    <span className="flex items-center gap-2 text-xs text-[#A3A3A3]">
                      <Calendar size={13} />{formatDate(contact.submitted_at)}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-4 border-t border-white/5 pt-4">
                    <p className="text-[#E5E5E5] text-sm leading-relaxed whitespace-pre-wrap flex-1">
                      {contact.message}
                    </p>
                    <button
                      onClick={() => {
                        setConfirm({
                          message: 'Apagar esta mensagem?',
                          onConfirm: async () => {
                            await axios.delete(`${BACKEND_URL}/api/admin/contacts/${contact.id}`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            setContacts(prev => prev.filter(c => c.id !== contact.id));
                          },
                        });
                      }}
                      className="text-[#333] hover:text-red-400 transition-colors flex-shrink-0 mt-0.5"
                      title="Apagar mensagem"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {alertMsg && (
        <div className="fixed top-6 right-6 z-50 bg-[#121212] border border-red-500/30 text-red-400 text-sm px-4 py-3 flex items-center gap-3 shadow-xl">
          {alertMsg}
          <button onClick={() => setAlertMsg('')} className="text-[#555] hover:text-white ml-2">✕</button>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirm(null)} />
          <div className="relative bg-[#121212] border border-white/10 p-6 w-full max-w-sm">
            <p className="text-sm text-[#E5E5E5] mb-6 leading-relaxed">{confirm.message}</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirm(null)}
                className="px-4 py-2 text-sm text-[#555] hover:text-white transition-colors">
                Cancelar
              </button>
              <button onClick={() => { confirm.onConfirm(); setConfirm(null); }}
                className="px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20 transition-colors">
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
